import * as yup from 'yup';
// matches PfProvider prisma enum
export var PremiumFinanceProvider;
(function (PremiumFinanceProvider) {
    PremiumFinanceProvider["AfcoBdSecure"] = "AFCO_BD_SECURE";
    PremiumFinanceProvider["AfcoLegacy"] = "AFCO_LEGACY";
    PremiumFinanceProvider["FIF"] = "FIF";
    PremiumFinanceProvider["IPFS"] = "IPFS";
    PremiumFinanceProvider["TestPfp"] = "TEST_PFP";
})(PremiumFinanceProvider || (PremiumFinanceProvider = {}));
// Note: Need to change this to have translations when we are going to operating PF in Canada.
// The company names could be different in Quebec. e.g AFCO in Canada = CAFO
export const premiumFinanceProviderLabels = {
    [PremiumFinanceProvider.AfcoBdSecure]: 'AFCO Direct (Formerly BankDirect)',
    [PremiumFinanceProvider.AfcoLegacy]: 'AFCO Direct',
    [PremiumFinanceProvider.FIF]: 'FIRST Insurance Funding',
    [PremiumFinanceProvider.IPFS]: 'Imperial PFS',
    [PremiumFinanceProvider.TestPfp]: 'Test PFP',
};
export var PaymentSchedule;
(function (PaymentSchedule) {
    PaymentSchedule["MONTHLY"] = "MONTHLY";
    PaymentSchedule["QUARTERLY"] = "QUARTERLY";
})(PaymentSchedule || (PaymentSchedule = {}));
export const financeableTransactionSchema = yup.object().shape({
    id: yup.string().required(),
    itemNumber: yup.string().required(),
    invoiceNumber: yup.string().required(),
    classCode: yup.string().required(),
    amountDue: yup
        .object()
        .shape({
        units: yup.number().required(),
        partialUnits: yup.number().required(),
    })
        .required(),
});
export const commonQuoteSchema = yup.object({
    pfProvider: yup.mixed().oneOf(Object.values(PremiumFinanceProvider)).optional(),
    shouldFail: yup.boolean().optional(),
    epicClientId: yup.string().required(),
    epicInvoiceNumbers: yup.array().of(yup.string().required()).min(1).required(),
});
export const checkForQuoteSchema = commonQuoteSchema.shape({
    context: yup.string().required(),
    merchantAccountId: yup.number().required(),
    paymentFlowSessionId: yup.string().optional(),
    insuredFirstName: yup.string().required(),
    insuredLastName: yup.string().required(),
    insuredEmail: yup.string().email().required(),
});
export const bindQuotePayloadSchema = yup.object().shape({
    quoteId: yup.number().integer().required(),
    unsignedPfa: yup.string().required(),
    bankAccountNumber: yup.string().optional(),
    bankRoutingNumber: yup.string().optional(),
    insuredSignature: yup
        .object()
        .shape({
        name: yup.string().required(),
        email: yup.string().required(),
        phone: yup.string().required(),
        dateTime: yup.string().required(),
    })
        .required(),
    servicingRolesEmails: yup.array().of(yup.string().email().required()).optional(),
});
export var PfGroupOrder;
(function (PfGroupOrder) {
    PfGroupOrder["PRIMARY"] = "PRIMARY";
    PfGroupOrder["BACKUP"] = "BACKUP";
    PfGroupOrder["SECONDARY_BACKUP"] = "SECONDARY_BACKUP";
})(PfGroupOrder || (PfGroupOrder = {}));
export const pfGroupConfigSchema = yup.object({
    pfProvider: yup.mixed().oneOf(Object.values(PremiumFinanceProvider)).required(),
    configOrder: yup.mixed().oneOf(Object.values(PfGroupOrder)).required(),
    minFinanceAmount: yup.number().integer().optional().nullable(),
    autoFinanceTransaction: yup.boolean().optional(),
    epicFinanceCompanyId: yup.string().required(),
});
export const afcoBdSecureCredentialSchema = yup.object().shape({
    afcoBdSecureUserName: yup.string().required(),
    afcoBdSecurePassword: yup.string().required(),
    afcoBdSecureImporterKey: yup.string().required(),
    afcoBdSecureAgencySearchCode: yup.string().required(),
});
export const afcoLegacyCredentialSchema = yup.object().shape({
    afcoLegacyAuthorizationKey: yup.string().required(),
    afcoLegacyCustomerCode: yup.string().required(),
    afcoLegacyEmailAddress: yup.string().required(),
    afcoLegacyEntityCode: yup.string().required(),
});
export const fifCredentialSchema = yup.object().shape({
    fifQuotingFor: yup.string().required(),
    fifPricingProgram: yup.string().required(),
    fifUserName: yup.string().required(),
    fifPassword: yup.string().required(),
});
export const ipfsCredentialSchema = yup.object().shape({
    ipfsLogin: yup.string().required(),
    ipfsAccessEmail: yup.string().required(),
    ipfsAccessCode: yup.string().required(),
    ipfsSystemCode: yup.string().required(),
    ipfsSubscriptionKey: yup.string().required(),
});
export const testPFPCredentialSchema = yup.object().shape({
    testPfpUserName: yup.string(),
    testPfpPassword: yup.string(),
});
const makeAllFieldsNotRequired = (schema) => {
    const updatedSchema = schema.clone();
    Object.keys(updatedSchema.fields).forEach((key) => (updatedSchema.fields[key] = updatedSchema.fields[key].notRequired()));
    return updatedSchema;
};
export const pfGroupAuthorizationSchema = yup.object().shape({
    id: yup.number(),
    epicAgencyId: yup.string().required(),
    epicBranchId: yup.string().required(),
    epicBranchName: yup.string().required(),
    epicAgencyName: yup.string().required(),
    pfGroupId: yup.number().required(),
    pfProvider: yup.mixed().oneOf(Object.values(PremiumFinanceProvider)).required(),
    isAppliedToAll: yup.boolean().optional(),
    // Listing credentials as a flat object because
    // TS cannot describe a type coming from a runtime
    // value used as a key. Make all fields not required
    // and use a custom test to determine whether or not
    // the received object is valid
    credentials: yup
        .object()
        // TODO pf-auth: use a reduce here
        .concat(makeAllFieldsNotRequired(afcoBdSecureCredentialSchema))
        .concat(makeAllFieldsNotRequired(afcoLegacyCredentialSchema))
        .concat(makeAllFieldsNotRequired(fifCredentialSchema))
        .concat(makeAllFieldsNotRequired(ipfsCredentialSchema))
        .concat(makeAllFieldsNotRequired(testPFPCredentialSchema))
        .required()
        .test('atLeastOnePfpCredentialSetGiven', 'At least one pfp credential set must be given', (obj) => [
        afcoBdSecureCredentialSchema,
        afcoLegacyCredentialSchema,
        fifCredentialSchema,
        ipfsCredentialSchema,
        testPFPCredentialSchema,
    ].some((schema) => schema.isValidSync(obj))),
});
export const pfProviderSchemas = {
    [PremiumFinanceProvider.AfcoBdSecure]: afcoBdSecureCredentialSchema,
    [PremiumFinanceProvider.AfcoLegacy]: afcoLegacyCredentialSchema,
    [PremiumFinanceProvider.IPFS]: ipfsCredentialSchema,
    [PremiumFinanceProvider.FIF]: fifCredentialSchema,
    [PremiumFinanceProvider.TestPfp]: testPFPCredentialSchema,
};
export const pfpAuthFields = {
    [PremiumFinanceProvider.AfcoBdSecure]: [
        { name: 'afcoBdSecureUserName', isEncrypted: false },
        { name: 'afcoBdSecurePassword', isEncrypted: true },
        { name: 'afcoBdSecureImporterKey', isEncrypted: true },
        { name: 'afcoBdSecureAgencySearchCode', isEncrypted: false },
    ],
    [PremiumFinanceProvider.AfcoLegacy]: [
        { name: 'afcoLegacyAuthorizationKey', isEncrypted: true },
        { name: 'afcoLegacyCustomerCode', isEncrypted: true },
        { name: 'afcoLegacyEmailAddress', isEncrypted: false },
        { name: 'afcoLegacyEntityCode', isEncrypted: true },
    ],
    [PremiumFinanceProvider.IPFS]: [
        { name: 'ipfsLogin', isEncrypted: false },
        { name: 'ipfsAccessEmail', isEncrypted: false },
        { name: 'ipfsAccessCode', isEncrypted: true },
        { name: 'ipfsSystemCode', isEncrypted: true },
        { name: 'ipfsSubscriptionKey', isEncrypted: true },
    ],
    [PremiumFinanceProvider.FIF]: [
        { name: 'fifQuotingFor', isEncrypted: true },
        { name: 'fifPricingProgram', isEncrypted: true },
        { name: 'fifUserName', isEncrypted: false },
        { name: 'fifPassword', isEncrypted: true },
    ],
    [PremiumFinanceProvider.TestPfp]: [
        { name: 'testPfpUserName', isEncrypted: false },
        { name: 'testPfpPassword', isEncrypted: true },
    ],
};
// optional fields, if not specified, will not be modified
export const updatePfGroupSchema = yup.object({
    id: yup.number().integer().required(),
    name: yup.string(),
    isActive: yup.boolean(),
    isDeleted: yup.boolean(),
    // epic folder structures area
    folderId: yup.string().optional().nullable(),
    folderPathDisplayName: yup.string().optional().nullable(),
    accessLevelId: yup.string().optional().nullable(),
    accessLevelName: yup.string().optional().nullable(),
    // email area
    agencyPaymentConfirmationEmails: yup.array().of(yup.string().email().required()).optional().nullable(),
    dailyBatchEmails: yup.array().of(yup.string().email().required()).optional().nullable(),
    // Authorization schema
    authorization: yup.array().of(pfGroupAuthorizationSchema.defined()).optional().default(undefined),
    // when configs is included, ALL used configs must be included (any not included will be deleted)
    configs: yup
        .object(Object.values(PfGroupOrder).reduce((o, order) => (Object.assign(Object.assign({}, o), { [order]: pfGroupConfigSchema.optional().default(undefined) })), {}))
        .optional()
        .default(undefined),
});
export const createPfGroupSchema = yup.object({
    name: yup.string().required(),
    isActive: yup.boolean().required(),
    epicDataContext: yup.string().required(),
});
export const addPfGroupEpicStructureSchema = yup.object({
    id: yup.number().integer().required(),
    epicAgencyId: yup.string().required(),
    epicBranchId: yup.string().required(),
    epicBranchName: yup.string(),
    epicAgencyName: yup.string(),
});
export const addPfGroupEpicStructuresSchema = yup.object({
    structures: yup.array(addPfGroupEpicStructureSchema).required(),
});
export const pfEpicStructureUpdateAssignedToAllSchema = yup.object({
    assignedToAllEpicStructures: yup.boolean().required(),
});
export const afcoLegacyAccountCreatedSchema = yup.object({
    QuoteNumber: yup.string().required(),
    AttributeValues: yup
        .object({
        AccountNumber: yup.string().required(),
        Agent: yup.string().optional(),
        AmountFinanced: yup.string().optional(),
        FirstPayDate: yup.string().optional(),
        NumberofPayments: yup.string().optional(),
        PutOnFileDate: yup.string().optional(),
        RemainingInstallments: yup.string().optional(),
        AutoPay: yup.string().optional(),
        ThirdPartyAutoPay: yup.string().optional(),
    })
        .required(),
    ZipCode: yup.string().optional(),
});
export const signPfaPayloadSchema = yup.object().shape({
    pfQuoteId: yup.number().required(),
    unsignedPfa: yup.string().required(),
    insuredName: yup.string().required(),
});
export const sendBillingInfoSchema = yup.object().shape({
    pfQuoteId: yup.number().required(),
    bankAccountNumber: yup.string().required(),
    bankRoutingNumber: yup.string().required(),
    name: yup.string().required(),
});
//# sourceMappingURL=premiumFinance.js.map